import { useEffect, useState } from "react";
import { X, Info, AlertTriangle, CheckCircle2 } from "lucide-react";
import { listActiveBroadcasts, type Broadcast } from "../../features/admin/api/adminApi";

const DISMISS_KEY = "dismissed_broadcasts";

function dismissed(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]") as string[];
  } catch {
    return [];
  }
}

const LEVEL = {
  info: { cls: "bg-ink-900 text-white", Icon: Info },
  warn: { cls: "bg-rose text-white", Icon: AlertTriangle },
  success: { cls: "bg-emerald text-white", Icon: CheckCircle2 },
} as const;

/** Dismissible in-app announcement banner (top of the Vault shell). */
export default function BroadcastBanner() {
  const [items, setItems] = useState<Broadcast[]>([]);

  useEffect(() => {
    listActiveBroadcasts()
      .then((all) => setItems(all.filter((b) => !dismissed().includes(b._id))))
      .catch(() => undefined);
  }, []);

  if (items.length === 0) return null;
  const b = items[0];
  const { cls, Icon } = LEVEL[b.level] ?? LEVEL.info;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, JSON.stringify([...dismissed(), b._id]));
    setItems((xs) => xs.filter((x) => x._id !== b._id));
  };

  return (
    <div className={`flex items-center gap-3 px-4 py-2 text-[12.5px] ${cls}`}>
      <Icon size={15} className="shrink-0 opacity-90" />
      <div className="min-w-0 flex-1">
        <span className="font-semibold">{b.title}</span>
        <span className="ml-2 opacity-90">{b.message}</span>
        {b.actionUrl && b.actionLabel && (
          <a href={b.actionUrl} target="_blank" rel="noopener noreferrer" className="ml-2 font-semibold underline underline-offset-2">
            {b.actionLabel}
          </a>
        )}
      </div>
      <button onClick={dismiss} className="rounded p-1 opacity-80 hover:opacity-100" aria-label="Dismiss">
        <X size={14} />
      </button>
    </div>
  );
}
